import React, { useLayoutEffect, useRef, useState } from 'react'
import type { RatingPolarity } from '@digital-twins/shared-types'

import { PlotBox } from '../components/chart-segment/PlotBox'
import type { ChartSegmentFigureResult } from '../components/chart-segment/ChartSegmentTabs'
import { ChartSegmentTitleSlot } from '../components/chart-segment/ChartSegmentTitleSlot'
import { TextClamp } from '../components/text/TextClamp'
import { KpiTile, kpiLocale } from '../components/data-display/KpiTile/KpiTile'
import { schemaBackedProseClampProps } from '../foundations/text-overflow-policy'
import { sanitizeNarrativeText } from '../foundations/text-sanitize'
import { clampArray } from '../foundations/slide-limits'
import { divergingLikertSlideBars } from '../plots/diverging-likert/adapters'
import type { DivergingLikertDrawnBar, DivergingLikertStoredData } from '../plots/diverging-likert/plot-plan'
import { readStampedDivergingLikert } from '../plots/diverging-likert/adapters'
import { planDivergingLikertRow } from '../plots/diverging-likert/plot-plan'
import { divergingLikertActiveView, divergingLikertAnchoredLabel } from '../plots/diverging-likert/stat-view-plan'
import { EditableText } from './EditableText'
import { EditableSlideText } from './EditableSlideText'
import { SlideNotice } from './SlideNotice'
import { registerSlide } from './registry'
import { BandLayout } from '../layouts/BandLayout'
import { presetSlot } from '../layouts/presetSlots'
import { SlotHeadline, SlotNarrative } from '../slots'
import { PLOT_CARD_FILL } from '../slots/PlotCard'
import {
  resolveRatingBar,
} from '../plots/diverging-likert/legacy/rating-likert-split'
import type {
  RatingBarLegendEntry,
} from '../plots/diverging-likert/bar-plan'
import {
  RATING_LIKERT_BAR_ROW_WIDTH_PX,
  RATING_LIKERT_COLUMN_GAP_PX,
  RatingLikertBarRow,
  RatingLikertBarRowLabel,
  staticStackColumns,
  staticStackRows,
} from '../plots/diverging-likert/RatingLikertBarRow'
import type { ChartSegmentInnerView } from '@digital-twins/shared-types'
import type { EditableSlideProps } from './contracts'
import type { RatingLikertSlideData } from './renderer-internal-types'

// legacy reader only: the narrative eyebrow is required, so a deck built today stamps it.
const LEGACY_EYEBROW = 'Likert rating'

type RatingLikertRow = NonNullable<RatingLikertSlideData['rows']>[number]

const RATING_LIKERT_SLIDE_TYPE = 'rating-likert' as const

/** The deck's row name: editable slide text, its full value as the chart label hint (W7 D5). */
function renderRatingLikertRowLabel(label: RatingLikertBarRowLabel) {
  return (
    <EditableSlideText
      value={label.value}
      field={label.field}
      editable={label.editable}
      onEdit={label.onEdit}
      title={label.value}
      chartLabel={{ hintIndex: label.rowIndex }}
      renderMode={label.renderMode}
      minLines={1}
      maxLines={label.maxLines}
      lineHeight="1.2"
      style={label.style}
    />
  )
}

const GRAY = 'var(--slide-text-muted)'
const BRAND_EMPHASIS = 'var(--slide-accent)'

/**
 * The slide's geometry is its band binding (plots plan Step 7, AE): the plot slot and
 * the band's columns from `packages/slide-schemas/layouts/` (band.json plus this
 * preset's overrides). The constants below are read from it, not restated.
 */
const PLOT_RECT = presetSlot(RATING_LIKERT_SLIDE_TYPE, 'plot').rect!
const HEADLINE = presetSlot(RATING_LIKERT_SLIDE_TYPE, 'headline').text!
const NARRATIVE = presetSlot(RATING_LIKERT_SLIDE_TYPE, 'narrative').text!
/** Figma deck frame height (`--ds-slide-height` in tokens.css). */
export const RATING_LIKERT_SLIDE_HEIGHT_PX = 787
export const RATING_LIKERT_PANEL_LEFT_PX = PLOT_RECT.x
export const RATING_LIKERT_PANEL_TOP_PX = PLOT_RECT.y
export const RATING_LIKERT_PANEL_BOTTOM_PX = RATING_LIKERT_SLIDE_HEIGHT_PX - PLOT_RECT.y - PLOT_RECT.h
/** Figma panel width for this slide. */
export const RATING_LIKERT_PANEL_WIDTH_PX = PLOT_RECT.w
/** Moved with the bar row (W7 D5): `plots/diverging-likert/RatingLikertBarRow`. */
export { RATING_LIKERT_BAR_ROW_WIDTH_PX, likertNameMaxLines } from '../plots/diverging-likert/RatingLikertBarRow'
export const RATING_LIKERT_ROWS_TOP_PX = 82
/**
 * The chart heading is the question, whole, above the rows. Its line budget
 * (`x-limits.text.title.maxLines`) is what the band between the heading's top and
 * `RATING_LIKERT_ROWS_TOP_PX` holds: 3 lines of 14px at 1.2 are 50.4px of the 51px
 * band. Two lines cut LP-05's 240-character question beside its scale key
 * (2026-10-14). `RatingLikertSlide.test.tsx` asserts the sum.
 */
export const RATING_LIKERT_CHART_TITLE_TOP_PX = 31
export const RATING_LIKERT_CHART_TITLE_FONT_SIZE_PX = 14
export const RATING_LIKERT_CHART_TITLE_LINE_HEIGHT = 1.2
/**
 * Bar height for a lone bar, baked or drawn under a cut.
 *
 * Supersedes the 90px two-row default the single bar borrowed. The single-row panel
 * now reaches the slide bottom like the comparison stack, which hands one bar the
 * whole `RATING_LIKERT_ROWS_CLIP_PX` track instead of 113px of it, and a 90px bar in a
 * 284px track is the dead space moved rather than removed. 180 is a judgement, not a
 * derivation: it doubles the bar's presence while keeping roughly a third of the track
 * as air above and below, which is what stops one bar reading as a filled panel.
 */
export const RATING_LIKERT_SINGLE_BAR_HEIGHT_PX = 180

/**
 * The 260px header block, and the two things stacked in its left column.
 *
 * The KPI tile replaced a three-tile row that sat in the RIGHT column, under copy that
 * already overflows it. The left column holds only the headline, so the tile goes there,
 * pinned to the bottom (frozen contract, Step 1). The budget has to be declared rather
 * than hoped for: a 3-line headline is `3 * 40 * 1.2 = 144px` and the block is 260px, so
 * the tile gets what is left and is clipped at it. `RatingLikertSlide.test.tsx` asserts
 * the sum, so a later type-scale change fails there instead of silently overlapping the
 * panel.
 */
export const RATING_LIKERT_HEADER_BLOCK_HEIGHT_PX = presetSlot(RATING_LIKERT_SLIDE_TYPE, 'lead').rect!.h
export const RATING_LIKERT_TITLE_MAX_HEIGHT_PX = 144
export const RATING_LIKERT_KPI_TILE_MAX_HEIGHT_PX = 104

/**
 * The insight bullets' own type, declared rather than repeated in the JSX below,
 * because the clip that keeps them whole is arithmetic on this line box.
 */
export const RATING_LIKERT_BULLET_FONT_SIZE_PX = 18
export const RATING_LIKERT_BULLET_LINE_HEIGHT = 1.5
export const RATING_LIKERT_BULLET_LINE_BOX_PX =
  RATING_LIKERT_BULLET_FONT_SIZE_PX * RATING_LIKERT_BULLET_LINE_HEIGHT

/** Sub-pixel slack, so a boundary that lands on the edge counts as fitting. */
const WHOLE_LINE_CLIP_TOLERANCE_PX = 1

/** One clipped child, measured from the top of the box that clips it. */
export interface WholeLineClipRow {
  top: number
  height: number
}

/**
 * The tallest whole-line height the bullets box may take, or `undefined` to take
 * its natural one.
 *
 * The right column is 648x260 and its copy can ask for 90 + 12 + 178 = 280. The
 * containment guard clips the surplus, but a clip at 260 cuts THROUGH the last
 * line's glyphs, which is visibly broken in a way that losing the line is not. So
 * the box is clipped at the last line boundary that still fits: a line either
 * appears whole or does not appear.
 *
 * Every boundary is a candidate, not just the row ends, because a two-line bullet
 * whose first line fits and whose second does not should keep the first.
 *
 * Pure and exported so the arithmetic is testable: jsdom reports 0 for every box
 * metric, so a rendered assertion could never exercise it.
 */
export function wholeLineClipHeight(
  rows: Array<WholeLineClipRow>,
  availableHeight: number,
  lineBoxPx: number = RATING_LIKERT_BULLET_LINE_BOX_PX,
): number | undefined {
  if (!rows.length || availableHeight <= 0 || lineBoxPx <= 0) {
    return undefined
  }

  let contentHeight = 0
  let clip = 0
  for (const row of rows) {
    contentHeight = Math.max(contentHeight, row.top + row.height)
    const lines = Math.max(1, Math.round(row.height / lineBoxPx))
    for (let line = 1; line <= lines; line += 1) {
      const boundary = row.top + line * lineBoxPx
      if (boundary <= availableHeight + WHOLE_LINE_CLIP_TOLERANCE_PX && boundary > clip) {
        clip = boundary
      }
    }
  }

  if (contentHeight <= availableHeight + WHOLE_LINE_CLIP_TOLERANCE_PX) {
    // Nothing is being cut, so nothing is clipped: the box keeps its own height.
    return undefined
  }
  // `clip === 0` means not even the first line fits, which this slide's budget
  // cannot produce; the column's own `overflow: hidden` stays the backstop rather
  // than this returning a zero height and erasing the bullets.
  return clip > 0 ? clip : undefined
}

/**
 * Clips the insight bullets to whole lines, from what the column actually granted.
 *
 * jsdom reports 0 for every box metric, so the measurement no-ops under test and the
 * box keeps its natural height there; `wholeLineClipHeight` carries the arithmetic
 * and is unit-tested directly.
 */
function useWholeLineClip(bullets?: Array<string>) {
  const boxRef = useRef<HTMLDivElement | null>(null)
  const [clipHeight, setClipHeight] = useState<number | undefined>(undefined)
  const bulletKey = (bullets || []).join('\u0000')

  useLayoutEffect(() => {
    const box = boxRef.current
    if (!box) {
      return
    }

    const measure = () => {
      const node = boxRef.current
      if (!node) {
        return
      }
      // The clip is lifted across the read, the way `isTextClampTruncated` lifts its
      // own: otherwise the second measurement would be of the height this hook last
      // wrote, and the box could only ever shrink.
      const applied = node.style.maxHeight
      node.style.maxHeight = 'none'
      const available = node.clientHeight
      const boxTop = node.getBoundingClientRect().top
      const rows = Array.from(node.children).map((child) => {
        const rect = (child as HTMLElement).getBoundingClientRect()
        return { top: rect.top - boxTop, height: rect.height }
      })
      node.style.maxHeight = applied
      setClipHeight(wholeLineClipHeight(rows, available))
    }

    measure()

    // A webfont that lands after mount changes the line box, and the clip has to
    // follow it. jsdom has no ResizeObserver, so this degrades to one measurement.
    if (typeof ResizeObserver === 'undefined') {
      return
    }
    const observer = new ResizeObserver(measure)
    observer.observe(box)
    return () => {
      observer.disconnect()
    }
  }, [bulletKey])

  return { boxRef, clipHeight }
}
/**
 * Bottom gutter under the rows stack. Sized to carry the band key
 * (16px/1.2 = 19.2px) plus breathing room above and below it.
 */
export const RATING_LIKERT_ROWS_BOTTOM_PX = 36
export const RATING_LIKERT_ROWS_CLIP_PX =
  RATING_LIKERT_SLIDE_HEIGHT_PX
  - RATING_LIKERT_PANEL_TOP_PX
  - RATING_LIKERT_PANEL_BOTTOM_PX
  - RATING_LIKERT_ROWS_TOP_PX
  - RATING_LIKERT_ROWS_BOTTOM_PX

function narrativeFromProps(props: RatingLikertSlideData) {
  const explicit = (props.actionSubtitle || '').replace(/\s+/g, ' ').trim()
  if (explicit) return explicit

  return ''
}

function LegendItem(props: { color: string; label: string }) {
  return (
    // Identified so a test can read the band names off one legend row at a time.
    // The legend is the slide's own whole-sample read (`props.legend`), drawn both
    // by the baked chart and, under a cut, by the tabbed chrome, so it is the
    // surface a leak of segment statistics into the slide would move.
    <div
      data-testid="rating-likert-legend-item"
      style={{ display: 'flex', alignItems: 'center', gap: 6, minWidth: 0 }}
    >
      <div style={{ width: 8, height: 8, borderRadius: 8, background: props.color, flex: '0 0 auto' }} />
      <TextClamp minLines={1} maxLines={1} lineHeight="1.2" style={{ fontSize: 16, color: GRAY }}>
        {props.label}
      </TextClamp>
    </div>
  )
}

export function versionRowBarHeight(rowCount: number) {
  if (rowCount <= 1) {
    return RATING_LIKERT_SINGLE_BAR_HEIGHT_PX
  }
  if (rowCount <= 2) {
    return 90
  }
  if (rowCount <= 3) {
    return 56
  }
  if (rowCount <= 4) {
    return 48
  }
  if (rowCount <= 6) {
    return 32
  }
  if (rowCount <= 7) {
    return 28
  }
  return 18
}

/**
 * Track heights are budgeted against `RATING_LIKERT_ROWS_CLIP_PX`, which lost 20px
 * to the scale-legend gutter. Every bucket from 2 to the schema max of 10 rows must
 * satisfy `rowCount * track + (rowCount - 1) * gap <= RATING_LIKERT_ROWS_CLIP_PX`.
 * The 4, 6 and 7 row buckets no longer fit at their old track heights. The 10 row
 * bucket stopped fitting as well (10 * 24 + 9 * 6 = 294 against a 284px clip), but
 * its track is unchanged: it fits again only because `versionRowGap` dropped from
 * 6 to 4 for the 8-and-up bucket.
 */
export function versionRowTrackHeight(rowCount: number) {
  if (rowCount <= 1) {
    return RATING_LIKERT_ROWS_CLIP_PX
  }
  if (rowCount <= 2) {
    return 113
  }
  if (rowCount <= 3) {
    return 72
  }
  if (rowCount <= 4) {
    return 60
  }
  if (rowCount <= 6) {
    return 42
  }
  if (rowCount <= 7) {
    return 34
  }
  return 24
}

export function versionRowGap(rowCount: number) {
  if (rowCount <= 4) {
    return 12
  }
  if (rowCount <= 7) {
    return 6
  }
  return 4
}

/**
 * Scale-end words for the band key. A missing end stays off that band, and so
 * does every band of a middle-is-best scale, which has no favourable end.
 */
function bandAnchor(
  band: RatingBarLegendEntry['band'],
  minLabel: string,
  maxLabel: string,
  polarity: RatingPolarity,
): string | null {
  if (polarity === 'middle_is_best') {
    return null
  }
  const higherIsBetter = polarity === 'higher_is_better'
  if (band === 'favorable') {
    return (higherIsBetter ? maxLabel : minLabel) || null
  }
  if (band === 'unfavorable') {
    return (higherIsBetter ? minLabel : maxLabel) || null
  }
  return null
}

/** "1 = No confidence at all", the survey tab's scale key. A missing end stays off. */
function scaleEnd(number: number | undefined, label: string | undefined): string | null {
  const words = (label || '').replace(/\s+/g, ' ').trim()
  if (typeof number !== 'number' || !Number.isFinite(number) || !words) {
    return null
  }
  return `${number} = ${words}`
}

function legendItemLabel(
  entry: RatingBarLegendEntry,
  minLabel: string,
  maxLabel: string,
  polarity: RatingPolarity,
): string {
  return divergingLikertAnchoredLabel(entry.label, bandAnchor(entry.band, minLabel, maxLabel, polarity))
}

interface RatingVersionRow {
  id: string
  label: string
  imageUrl?: string | null
  bar: DivergingLikertDrawnBar
  /** False when the row's plot stamped no net: the net column stays empty. */
  hasNet: boolean
}

interface RatingDivergingPlotProps {
  bar: DivergingLikertDrawnBar
  chartTitle: string
  legend: Array<RatingBarLegendEntry>
  /** Localized through `deck_label`; a missing label hides the net rather than inventing one. */
  netLabel?: string | null
  versionRows?: Array<RatingVersionRow>
  /** The edit path of a row label: the plot's row when the slide draws a stamped plot. */
  rowLabelField: (index: number) => string
  slide: Record<string, unknown>
  buckets: Array<{ id: string; label: string }>
  displayLabels?: Record<string, string>
  scaleMin?: number
  scaleMax?: number
  scaleMinLabel?: string
  scaleMaxLabel?: string
  polarity?: RatingPolarity
  editable?: boolean
  onEdit?: (field: string, value: string) => void
  /**
   * D17: threaded down from the slide, so the clamps that offer truncation tooltips
   * fall back to the native `title` in a static export.
   */
  renderMode?: 'interactive' | 'static'
}

/**
 * One segment, drawn with the slide's own bar row rather than the generic inner view.
 * A breakdown is still this chart — so it keeps this chart's bar height instead of
 * collapsing to a stripe with the panel empty around it.
 *
 * "This chart's bar height" is the version-row budget, not the 90/113 default: with
 * two rows or fewer they are the same number, but a five-row chart bakes its bars at
 * 32/42 and a segment drawn at 90/113 is three times the presence of the row it
 * replaces. Both heights come from the same `versionRow*` functions the baked stack
 * uses, so they cannot drift apart.
 */
function renderRatingLikertSegmentFigure(
  view: ChartSegmentInnerView,
  barHeight: number,
  trackHeight: number,
  displayLabels?: Record<string, string>,
  plot?: unknown,
): ChartSegmentFigureResult | null {
  if (view.kind !== 'likert-diverging') {
    return null
  }
  const nestedRows = Array.isArray(view.rows) ? view.rows : []
  // The tab's plot Python stamped (W6-G): the bars through the slide's own row
  // model, as the baked slide draws its plot. A response from an analysis
  // service that predates the tab plot keeps the reader below.
  const stamped = readStampedDivergingLikert(plot)
  if (stamped) {
    return renderStampedSegmentFigure(stamped, nestedRows, barHeight, trackHeight, displayLabels)
  }
  if (nestedRows.length > 0) {
    return {
      flushLeft: true,
      node: (
        <div
          data-testid="rating-likert-segment-rows"
          style={{ display: 'flex', flexDirection: 'column', gap: 8 }}
        >
          {nestedRows.map((row, index) => {
            const bar = resolveRatingBar(
              {
                ...view,
                buckets: row.buckets,
                segments: row.segments,
                ratingStatistics: row.ratingStatistics ?? view.ratingStatistics,
              },
              displayLabels,
            )
            if (bar.dataStatus === 'unsupported') {
              return (
                <div key={row.id} data-testid={`rating-likert-segment-row-${index}`}>
                  {row.label}
                </div>
              )
            }
            if (!bar.plan) {
              return (
                <div key={row.id} data-testid={`rating-likert-segment-row-${index}`}>
                  {row.label}
                </div>
              )
            }
            return (
              <RatingLikertBarRow
                notAvailableLabel={displayLabels?.notAvailable}
                key={row.id}
                renderLabel={renderRatingLikertRowLabel}
                testId={`rating-likert-segment-row-${index}`}
                rowIndex={index}
                label={row.label}
                plan={bar.plan}
                dataStatus={bar.dataStatus}
                barHeight={barHeight}
                trackHeight={trackHeight}
              />
            )
          })}
        </div>
      ),
    }
  }
  // A drilldown response can carry `segments` without `ratingStatistics`: the
  // producer stamps the bar unconditionally while the statistics stay behind the
  // request's `ratingSemanticsVersion` gate. A response cached before the field
  // existed carries neither, and falls to the legacy bar through the same call.
  const bar = resolveRatingBar(view, displayLabels)
  if (bar.dataStatus === 'unsupported') {
    return { node: displayLabels?.unsupportedScale ? <SlideNotice>{displayLabels.unsupportedScale}</SlideNotice> : null }
  }
  if (!bar.plan) {
    return null
  }
  return {
    // RatingLikertBarRow is a hardcoded RATING_LIKERT_BAR_ROW_WIDTH_PX, which is
    // exactly the panel minus the gear's clearance and leaves nothing for the tabs
    // column's left gutter. Opt this figure out rather than dropping the gutter for
    // the quadrant, matrix and likert inner views that do need it.
    flushLeft: true,
    node: (
      <RatingLikertBarRow
        notAvailableLabel={displayLabels?.notAvailable}
        renderLabel={renderRatingLikertRowLabel}
        testId="rating-likert-segment-row"
        rowIndex={0}
        plan={bar.plan}
        dataStatus={bar.dataStatus}
        barHeight={barHeight}
        trackHeight={trackHeight}
      />
    ),
  }
}

/**
 * The segment figure from the tab's stamped diverging-likert plot. Version rows
 * keep the view's order and labels and take their bar from the plot row with the
 * same id; a row the segment never answered has no plot row and prints its label
 * alone, as before. A flat tab draws the plot's one row.
 */
function renderStampedSegmentFigure(
  data: DivergingLikertStoredData,
  nestedRows: Array<{ id: string; label: string }>,
  barHeight: number,
  trackHeight: number,
  displayLabels?: Record<string, string>,
): ChartSegmentFigureResult | null {
  if (data.status === 'unsupported') {
    const notice = data.labels.unsupported_rating_scale
    return { node: notice ? <SlideNotice>{notice}</SlideNotice> : null }
  }
  if (nestedRows.length > 0) {
    const plotRows = new Map(data.rows.map((row) => [row.id, row]))
    return {
      flushLeft: true,
      node: (
        <div
          data-testid="rating-likert-segment-rows"
          style={{ display: 'flex', flexDirection: 'column', gap: 8 }}
        >
          {nestedRows.map((row, index) => {
            const plotRow = plotRows.get(row.id)
            const bar = plotRow ? planDivergingLikertRow(plotRow, data, divergingLikertActiveView(data)) : null
            if (!bar?.plan) {
              return (
                <div key={row.id} data-testid={`rating-likert-segment-row-${index}`}>
                  {row.label}
                </div>
              )
            }
            return (
              <RatingLikertBarRow
                notAvailableLabel={displayLabels?.notAvailable}
                key={row.id}
                renderLabel={renderRatingLikertRowLabel}
                testId={`rating-likert-segment-row-${index}`}
                rowIndex={index}
                label={row.label}
                plan={bar.plan}
                dataStatus={bar.dataStatus}
                barHeight={barHeight}
                trackHeight={trackHeight}
              />
            )
          })}
        </div>
      ),
    }
  }
  const only = data.rows[0]
  const bar = only ? planDivergingLikertRow(only, data, divergingLikertActiveView(data)) : null
  if (!bar?.plan) {
    return null
  }
  return {
    flushLeft: true,
    node: (
      <RatingLikertBarRow
        notAvailableLabel={displayLabels?.notAvailable}
        renderLabel={renderRatingLikertRowLabel}
        testId="rating-likert-segment-row"
        rowIndex={0}
        plan={bar.plan}
        dataStatus={bar.dataStatus}
        barHeight={barHeight}
        trackHeight={trackHeight}
      />
    ),
  }
}

function RatingDivergingPlot(props: RatingDivergingPlotProps) {
  const chartTitleClamp = schemaBackedProseClampProps('title', RATING_LIKERT_SLIDE_TYPE, {
    lineHeight: String(RATING_LIKERT_CHART_TITLE_LINE_HEIGHT),
  })
  const versionRows = props.versionRows || []
  const hasVersionRows = versionRows.length >= 1
  const rowCount = versionRows.length
  const barHeight = versionRowBarHeight(rowCount)
  const trackHeight = versionRowTrackHeight(rowCount)
  const rowGap = versionRowGap(rowCount)
  const staticStack = props.renderMode === 'static'
  const renderSegmentFigure = React.useCallback(
    (view: ChartSegmentInnerView, displayLabels?: Record<string, string>, plot?: unknown) =>
      renderRatingLikertSegmentFigure(
        view,
        barHeight,
        trackHeight,
        displayLabels ?? props.displayLabels,
        plot,
      ),
    [props.displayLabels, barHeight, trackHeight],
  )

  // One key, built from the bands actually on the bar, used by the baked chart and
  // by a cut alike. It replaces two legends that disagreed: the header took
  // `geometryLegendLabels`, which named its two neutral halves twice, and the
  // drilldown took the measured labels -- and both nodes answered to the same
  // `data-testid`, so no test could say which one it had selected.
  const legendEntries = props.legend
  const legendLabel = (entry: RatingBarLegendEntry) => legendItemLabel(
    entry,
    props.scaleMinLabel ?? '',
    props.scaleMaxLabel ?? '',
    props.polarity ?? 'higher_is_better',
  )
  const segmentLegend = (
    <div
      data-testid="rating-likert-segment-legend"
      style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 16 }}
    >
      {legendEntries.map((entry) => (
        <LegendItem key={entry.band} color={entry.color} label={legendLabel(entry)} />
      ))}
    </div>
  )
  const meaningKey = (
    <div
      data-testid="rating-likert-legend"
      style={{
        position: 'absolute',
        left: 32,
        right: 32,
        top: RATING_LIKERT_ROWS_TOP_PX + RATING_LIKERT_ROWS_CLIP_PX + 10,
        display: 'flex',
        flexWrap: 'wrap',
        alignItems: 'center',
        gap: 16,
        minWidth: 0,
      }}
    >
      {legendEntries.map((entry) => (
        <LegendItem key={entry.band} color={entry.color} label={legendLabel(entry)} />
      ))}
    </div>
  )

  const lowEnd = scaleEnd(props.scaleMin, props.scaleMinLabel)
  const highEnd = scaleEnd(props.scaleMax, props.scaleMaxLabel)
  const scaleKey = lowEnd || highEnd ? (
    <div
      data-testid="rating-likert-scale-key"
      style={{
        display: 'flex',
        flexWrap: 'wrap',
        justifyContent: 'flex-end',
        gap: 16,
        flex: '0 1 auto',
        maxWidth: '46%',
        fontSize: 16,
        lineHeight: 1.2,
        color: GRAY,
      }}
    >
      {lowEnd ? <span data-testid="rating-likert-scale-key-low">{lowEnd}</span> : null}
      {highEnd ? <span data-testid="rating-likert-scale-key-high">{highEnd}</span> : null}
    </div>
  ) : null

  const plotHeader = (
    <ChartSegmentTitleSlot
      contentPadRight={0}
      style={{
        position: 'absolute',
        left: 32,
        top: RATING_LIKERT_CHART_TITLE_TOP_PX,
        right: 0,
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'flex-start',
        gap: 24,
      }}
    >
      <EditableSlideText
        data-testid="rating-likert-chart-title"
        value={props.chartTitle}
        field="title"
        editable={props.editable}
        onEdit={props.onEdit}
        tooltipOnTruncate
        renderMode={props.renderMode}
        minLines={chartTitleClamp.minLines}
        maxLines={chartTitleClamp.maxLines}
        lineHeight={chartTitleClamp.lineHeight}
        title={props.chartTitle}
        style={{
          fontSize: RATING_LIKERT_CHART_TITLE_FONT_SIZE_PX,
          fontWeight: 600,
          color: 'var(--slide-text-primary)',
          flex: '1 1 0%',
          minWidth: 0,
        }}
      />
      {scaleKey}
    </ChartSegmentTitleSlot>
  )

  if (hasVersionRows) {
    return (
      <section
        data-testid="rating-likert-panel"
        style={{
          position: 'absolute',
          left: RATING_LIKERT_PANEL_LEFT_PX,
          top: RATING_LIKERT_PANEL_TOP_PX,
          width: RATING_LIKERT_PANEL_WIDTH_PX,
          bottom: RATING_LIKERT_PANEL_BOTTOM_PX,
          borderRadius: 10,
          background: PLOT_CARD_FILL,
          overflow: 'hidden',
        }}
      >
        <PlotBox
          slide={props.slide}
          slideType="rating-likert"
          columns={props.buckets}
          rows={versionRows.map((row) => ({ id: row.id, label: row.label }))}
          displayLabels={props.displayLabels}
          title={props.chartTitle}
          renderSegmentFigure={renderSegmentFigure}
          segmentLegend={segmentLegend}
        >
          {plotHeader}
          <div
            data-testid="rating-likert-rows"
            style={{
              position: 'absolute',
              left: 32,
              top: RATING_LIKERT_ROWS_TOP_PX,
              width: RATING_LIKERT_BAR_ROW_WIDTH_PX,
              height: RATING_LIKERT_ROWS_CLIP_PX,
              minHeight: 0,
              overflow: 'hidden',
              // A static render draws the row names whole (wave D): the rows share
              // one grid whose name column widens for long names and whose rows grow
              // for a name out of the room left inside the same clip.
              ...(staticStack
                ? {
                    display: 'grid',
                    gridTemplateColumns: staticStackColumns(),
                    gridAutoRows: staticStackRows(trackHeight),
                    columnGap: RATING_LIKERT_COLUMN_GAP_PX,
                    rowGap,
                    alignContent: 'start',
                  }
                : {
                    display: 'flex',
                    flexDirection: 'column',
                    gap: rowGap,
                    justifyContent: 'flex-start',
                  }),
            }}
          >
            {versionRows.map((row, index) => (
              row.bar.plan ? (
                <RatingLikertBarRow
                  notAvailableLabel={props.displayLabels?.notAvailable}
                  key={row.id || `row-${index}`}
                  renderLabel={renderRatingLikertRowLabel}
                  testId={`rating-likert-row-${index}`}
                  rowIndex={index}
                  label={row.label}
                  imageUrl={row.imageUrl}
                  labelField={props.rowLabelField(index)}
                  editable={props.editable}
                  onEdit={props.onEdit}
                  renderMode={props.renderMode}
                  inStaticStack={staticStack}
                  plan={row.bar.plan}
                  dataStatus={row.bar.dataStatus}
                  // Only a comparison stack prints a net: it is what one row says
                  // against another. A lone bar has nothing to be net OF.
                  netLabel={rowCount > 1 && row.hasNet ? props.netLabel : null}
                  barHeight={barHeight}
                  trackHeight={trackHeight}
                  polarity={props.polarity}
                />
              ) : null
            ))}
          </div>
          {meaningKey}
        </PlotBox>
      </section>
    )
  }

  // Supersedes the 225px flat panel (`787 - 365 - 225` left exactly 197px of white
  // below it, 20px of which was the slide's own bottom gutter and 177px of which was
  // nothing at all). A single bar is not a smaller chart than a comparison stack, so
  // its panel reaches the same bottom and its bar is budgeted from the same clip.
  return (
    <section
      data-testid="rating-likert-panel"
      style={{
        position: 'absolute',
        left: RATING_LIKERT_PANEL_LEFT_PX,
        top: RATING_LIKERT_PANEL_TOP_PX,
        width: RATING_LIKERT_PANEL_WIDTH_PX,
        bottom: RATING_LIKERT_PANEL_BOTTOM_PX,
        borderRadius: 10,
        background: PLOT_CARD_FILL,
        overflow: 'hidden',
      }}
    >
      <PlotBox
        slide={props.slide}
        slideType="rating-likert"
        columns={props.buckets}
        displayLabels={props.displayLabels}
        title={props.chartTitle}
        renderSegmentFigure={renderSegmentFigure}
        segmentLegend={segmentLegend}
      >
        {plotHeader}
        <div
          data-testid="rating-likert-chart"
          style={{
            position: 'absolute',
            left: 32,
            top: RATING_LIKERT_ROWS_TOP_PX,
            width: RATING_LIKERT_BAR_ROW_WIDTH_PX,
            height: RATING_LIKERT_ROWS_CLIP_PX,
          }}
        >
          {/* Single-row fallback is behaviour-identical, not pixel-identical (1px barTop + wrapper). */}
          {props.bar.plan ? (
            <RatingLikertBarRow
              notAvailableLabel={props.displayLabels?.notAvailable}
              renderLabel={renderRatingLikertRowLabel}
              rowIndex={0}
              plan={props.bar.plan}
              dataStatus={props.bar.dataStatus}
              barHeight={barHeight}
              trackHeight={trackHeight}
              polarity={props.polarity}
            />
          ) : null}
        </div>
        {meaningKey}
      </PlotBox>
    </section>
  )
}

function RatingLikertSlideComponent(props: RatingLikertSlideData & EditableSlideProps) {
  // D17: forwarded to the diverging plot, which owns the clamps that offer truncation
  // tooltips. A static export cannot open one, so they fall back to the native `title`.
  const { editable, onEdit, renderMode } = props
  const resolvedActionTitle = sanitizeNarrativeText(props.actionTitle)
  const { boxRef: insightBulletsRef, clipHeight: insightBulletsClipHeight } =
    useWholeLineClip(props.insightBullets)
  const buckets = clampArray(props.buckets || [], RATING_LIKERT_SLIDE_TYPE, 'buckets')
  // The bars come from the plot Python stamped (plots plan P); a stored slide
  // without `plot` draws through the legacy reader (today's resolver).
  const drawn = divergingLikertSlideBars(props)
  const versionRows = drawn.rows
  // A stamped slide draws its rows from the plot (one to one with the stored rows): a
  // label edit writes the plot's row and the stored row alike, so a reader of either
  // keeps it (as matrix-rating does).
  const rowLabelField = (index: number) => (drawn.stamped ? `plot.data.rows.${index}.label` : `rows.${index}.label`)
  const onPlotEdit = onEdit
    ? (field: string, value: string) => {
        onEdit(field, value)
        const plotRow = /^plot\.data\.rows\.(\d+)\.label$/.exec(field)
        if (plotRow) {
          onEdit(`rows.${plotRow[1]}.label`, value)
        }
      }
    : undefined
  // The same 85-char, 3-line budget the exec-summary strip reads, declared on this
  // slide's own schema under the shared `kpiSentence` budgetKey. Reusing the LIMIT is
  // the decision; reusing the exec-summary text BOX is not (frozen contract F6) -- that
  // box describes a 424px inline run in a different column.
  const kpiSentenceClamp = schemaBackedProseClampProps('kpiSentence', RATING_LIKERT_SLIDE_TYPE, {
    lineHeight: 'var(--ds-lh-snug)',
  })
  const bar = drawn.bar
  // Retired exactly when `hasRetiredRatingScale` says so: the same read's status.
  if (drawn.status === 'unsupported') return null
  const narrative = narrativeFromProps(props)
  const chartTitle = props.title || ''
  const scaleMinLabel = (props.scale.minLabel || '').replace(/\s+/g, ' ').trim()
  const scaleMaxLabel = (props.scale.maxLabel || '').replace(/\s+/g, ' ').trim()

  return (
    <BandLayout
      footerText={props.footerText}
      preset={RATING_LIKERT_SLIDE_TYPE}
      eyebrow={props.eyebrow || LEGACY_EYEBROW}
      clientLogoUrl={props.clientLogoUrl ?? null}
      testIds={{ layout: 'rating-likert-layout', band: 'rating-likert-top' }}
      band={
        <>
          {/*
            Left column: the headline, and the KPI tile pinned under it.

            `marginTop: 'auto'` is what pins it -- the tile sits on the block's bottom
            edge whatever the headline wraps to, so the figure lands on one line across
            every slide in a deck instead of floating with the title's length. The
            column is capped and clipped at the block height because the two are
            budgeted against each other (see RATING_LIKERT_TITLE_MAX_HEIGHT_PX): nothing
            here may reach the panel 32px below.
          */}
          <div
            data-testid="rating-likert-header-left"
            style={{
              display: 'flex',
              flexDirection: 'column',
              minWidth: 0,
              height: RATING_LIKERT_HEADER_BLOCK_HEIGHT_PX,
              overflow: 'hidden',
            }}
          >
            <SlotHeadline text={resolvedActionTitle || ''} maxLines={HEADLINE.maxLines} width={HEADLINE.width} editable={editable} onEdit={onEdit} />
            {props.kpi ? (
              <div
                style={{
                  marginTop: 'auto',
                  maxHeight: RATING_LIKERT_KPI_TILE_MAX_HEIGHT_PX,
                  overflow: 'hidden',
                }}
              >
                <KpiTile
                  kpi={props.kpi}
                  clamp={kpiSentenceClamp}
                  locale={kpiLocale(props)}
                  testId="rating-likert-kpi"
                  valueTestId="rating-likert-kpi-value"
                  copyTestId="rating-likert-kpi-copy"
                  sentenceTestId="rating-likert-kpi-sentence"
                  // The slide's own figures are ink, so the tile's numeral is ink too
                  // rather than the exec strip's data accent, which beside a black
                  // headline would read as a second emphasis with nothing to emphasise.
                  valueColor="var(--slide-text-primary)"
                />
              </div>
            ) : null}
          </div>

          {/*
            Right column: subtitle and bullets, contained.

            This column already overflowed its 260px before the KPI tile existed: a
            3-line subtitle is 90px, three 2-line bullets are 178px, and only the
            slide's outer box clipped anything -- so the surplus painted UNDER the
            translucent panel, which is drawn after it. Containing the column here is
            the guard: the copy can no longer reach the panel whatever it holds.

            Containment alone still cut glyphs. 90 + 12 + 178 = 280 in a 260px box, and
            both children shrank by default, so the residual 20px was taken
            proportionally: about 7px off the bottom of the subtitle's last line and
            13px off the third bullet's. Two half-lines is worse than one whole one.

            So the subtitle does not shrink at all, the bullet rows do not shrink
            either, and the bullets box is clipped to the last WHOLE line that fits
            (`wholeLineClipHeight`). A line now either appears complete or does not
            appear. `actionSubtitle.maxLines` and `insightBullet.maxLines` are schema
            budgets and are deliberately untouched.
          */}
          <div
            data-testid="rating-likert-header-right"
            style={{
              display: 'flex',
              flexDirection: 'column',
              gap: 12,
              minWidth: 0,
              // A static render draws the copy whole (wave D): the block grows past
              // its box instead of clipping it.
              ...(renderMode === 'static'
                ? { minHeight: RATING_LIKERT_HEADER_BLOCK_HEIGHT_PX }
                : { height: RATING_LIKERT_HEADER_BLOCK_HEIGHT_PX, minHeight: 0, overflow: 'hidden' }),
            }}
          >
            <SlotNarrative
              value={narrative}
              clamp={{ minLines: 1, maxLines: NARRATIVE.maxLines, lineHeight: '1.5' }}
              testId="rating-likert-narrative"
              // The subtitle keeps every line the schema grants it (the slot never
              // shrinks): shrinking cut through the glyphs rather than the text.
              style={{ width: 648 }}
              editable={editable}
              onEdit={onEdit}
              renderMode={props.renderMode}
            />
            {props.insightBullets?.length ? (
              <div
                ref={insightBulletsRef}
                data-testid="rating-likert-insight-bullets"
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 8,
                  minHeight: 0,
                  // A static render draws every bullet whole (wave D): no whole-line clip.
                  ...(renderMode === 'static'
                    ? {}
                    : { overflow: 'hidden', maxHeight: insightBulletsClipHeight }),
                }}
              >
                {props.insightBullets.map((bullet, i) => (
                  <div
                    key={i}
                    // No shrink: the box above clips whole lines off the stack, which
                    // a row that squashed itself would defeat by losing part of a line
                    // in every bullet instead of a whole line in one.
                    style={{ display: 'grid', gridTemplateColumns: '8px 1fr', columnGap: 12, alignItems: 'start', flexShrink: 0 }}
                  >
                    <span
                      aria-hidden="true"
                      style={{
                        color: BRAND_EMPHASIS,
                        fontSize: RATING_LIKERT_BULLET_FONT_SIZE_PX,
                        lineHeight: String(RATING_LIKERT_BULLET_LINE_HEIGHT),
                      }}
                    >
                      •
                    </span>
                    {editable && onEdit ? (
                      <EditableText
                        value={bullet}
                        field={`insightBullets.${i}`}
                        editable
                        onEdit={onEdit}
                        tag="span"
                        style={{ fontSize: 18, color: 'var(--slide-text-primary)', lineHeight: '1.5' }}
                      />
                    ) : (
                      <TextClamp
                        minLines={1}
                        maxLines={2}
                        lineHeight={RATING_LIKERT_BULLET_LINE_HEIGHT}
                        renderMode={renderMode}
                        style={{ fontSize: RATING_LIKERT_BULLET_FONT_SIZE_PX, color: 'var(--slide-text-primary)' }}
                      >
                        {bullet}
                      </TextClamp>
                    )}
                  </div>
                ))}
              </div>
            ) : null}
          </div>
        </>
      }
    >

        <RatingDivergingPlot
          bar={bar}
          chartTitle={chartTitle}
          legend={drawn.legend}
          netLabel={drawn.netLabel}
          scaleMin={props.scale.min}
          scaleMax={props.scale.max}
          scaleMinLabel={scaleMinLabel}
          scaleMaxLabel={scaleMaxLabel}
          polarity={props.scale.polarity}
          versionRows={versionRows}
          rowLabelField={rowLabelField}
          slide={props as unknown as Record<string, unknown>}
          buckets={buckets.map((bucket) => ({ id: bucket.id, label: bucket.label }))}
          displayLabels={props.displayLabels}
          editable={editable}
          onEdit={onPlotEdit}
          renderMode={renderMode}
        />
    </BandLayout>
  )
}

export const RatingLikertSlide = registerSlide('rating-likert', RatingLikertSlideComponent)
